import axios from 'axios';

// API Base URL - backend на rugoplaydonate.ru
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://rugoplaydonate.ru';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export const getAccessToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const setTokens = (accessToken, refreshToken) => {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// Request interceptor - add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token } = response.data;
          setTokens(access_token, refresh_token);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed - clear tokens and redirect to login
          clearTokens();
          window.location.href = '/auth';
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

// ========================
// Auth API
// ========================

export const authAPI = {
  /**
   * Register a new user
   * @param {Object} data - { email, password, first_name? }
   * @returns {Promise} Response with message
   */
  register: async (data) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  /**
   * Login with email and password
   * @param {Object} data - { email, password }
   * @returns {Promise} Response with tokens
   */
  login: async (data) => {
    const response = await api.post('/auth/login', data);
    const { access_token, refresh_token } = response.data;
    setTokens(access_token, refresh_token);
    return response.data;
  },

  /**
   * Logout - clear tokens
   */
  logout: () => {
    clearTokens();
  },

  /**
   * Get current user profile
   * @returns {Promise} User data
   */
  getProfile: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  /**
   * Update user profile
   * @param {Object} data - { first_name?, email? }
   * @returns {Promise} Updated user data
   */
  updateProfile: async (data) => {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },

  /**
   * Verify email with token
   * @param {string} token - Verification token
   * @returns {Promise} Response with message
   */
  verifyEmail: async (token) => {
    const response = await api.get(`/auth/verify-email/${token}`);
    return response.data;
  },

  /**
   * Request password reset
   * @param {Object} data - { email }
   * @returns {Promise} Response with message
   */
  forgotPassword: async (data) => {
    const response = await api.post('/auth/forgot-password', data);
    return response.data;
  },

  /**
   * Reset password with token
   * @param {string} token - Reset token
   * @param {Object} data - { password }
   * @returns {Promise} Response with message
   */
  resetPassword: async (token, data) => {
    const response = await api.post(`/auth/reset-password/${token}`, data);
    return response.data;
  },

  /**
   * Resend verification email
   * @param {Object} data - { email }
   * @returns {Promise} Response with message
   */
  resendVerification: async (data) => {
    const response = await api.post('/auth/resend-verification', data);
    return response.data;
  },

  /**
   * Verify email change with token
   * @param {string} token - Verification token
   * @returns {Promise} Response with message
   */
  verifyEmailChange: async (token) => {
    const response = await api.get(`/auth/verify-email-change/${token}`);
    return response.data;
  },

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated: () => {
    return !!getAccessToken();
  },

  /**
   * Get Google OAuth URL
   * @returns {string} Google OAuth redirect URL
   */
  getGoogleAuthUrl: () => {
    return `${API_BASE_URL}/auth/google`;
  },

  /**
   * Handle Google OAuth callback - save tokens from URL params
   * @param {string} accessToken - Access token from Google callback
   * @param {string} refreshToken - Refresh token from Google callback
   */
  handleGoogleCallback: (accessToken, refreshToken) => {
    setTokens(accessToken, refreshToken);
  },

  /**
   * Get VK OAuth URL
   * @returns {string} VK OAuth redirect URL
   */
  getVKAuthUrl: () => {
    return `${API_BASE_URL}/auth/vk`;
  },

  /**
   * Handle VK OAuth callback - save tokens from URL params
   * @param {string} accessToken - Access token from VK callback
   * @param {string} refreshToken - Refresh token from VK callback
   */
  handleVKCallback: (accessToken, refreshToken) => {
    setTokens(accessToken, refreshToken);
  },

  /**
   * Get Telegram auth link
   * @returns {Promise} Response with bot_link and token
   */
  getTelegramAuthLink: async () => {
    const response = await api.get('/auth/telegram/link');
    return response.data;
  },

  /**
   * Verify Telegram authentication
   * @param {Object} data - { payload, telegram_id, first_name, last_name, username, photo_url }
   * @returns {Promise} Response with tokens
   */
  verifyTelegramAuth: async (data) => {
    const response = await api.post('/auth/telegram/verify', data);
    return response.data;
  },

  /**
   * Upload user avatar
   * @param {File} file - Avatar image file
   * @returns {Promise} Updated user data
   */
  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/auth/upload-avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// ========================
// Website API (Orders, etc.)
// ========================

export const websiteAPI = {
  /**
   * Get user's orders
   * @returns {Promise} Orders data
   */
  getOrders: async () => {
    const response = await api.get('/website/orders');
    return response.data;
  },

  /**
   * Get platform statistics (users count and completed payments count)
   * @returns {Promise} Stats data
   */
  getStats: async () => {
    const response = await api.get('/website/stats');
    return response.data;
  },
};

export default api;

